import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import './MiniIOTPage.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3009/api/v1';

type CommandTarget = 'router' | 'XTAM' | 'all';
type DeviceStatus = 'ONLINE' | 'OFFLINE' | 'ONLINE_WITH_ISSUE' | 'MAINTENANCE';

interface MiniotInventory {
  id: number;
  serialDevice: string | null;
  idModel: number | null;
  modelName: string | null;
  mac: string | null;
  firmware: string | null;
  inventoryStateId: number | null;
  isActive: boolean;
}

interface MiniotStatus {
  id: number;
  deviceMiniotId: number;
  inventoryMiniotId: number | null;
  siteId: number;
  status: DeviceStatus;
  lastHeartbeatAt: string | null;
  lastTopicReceived: string | null;
  createdAt: string;
  updatedAt: string;
}

interface MiniotDevice {
  id: number;
  inventoryMiniotId: number;
  siteId: number;
  createdAt: string;
  updatedAt: string;
  inventory: MiniotInventory | null;
  status: MiniotStatus | null;
}

interface Site {
  id: number;
  name: string;
  address: string;
  neighborhood?: string;
  location?: string;
  active: boolean;
  devices?: {
    miniots?: MiniotDevice[];
    cameras?: any[];
    xtamVideos?: any[];
    buttons?: any[];
    sirens?: any[];
    routers?: any[];
  };
}

interface RestartProgress {
  target: CommandTarget;
  progress: number;
  isActive: boolean;
}

interface CommandReport {
  success: boolean;
  target: CommandTarget;
  message: string;
  timestamp: Date;
  siteName: string;
  deviceSerial: string;
  commandId?: string;
}

interface ActuatorClient {
  ip?: string;
  mac?: string;
}

interface ActuatorStatus {
  count?: number;
  status?: string;
  clients?: ActuatorClient[];
}

interface StatusHistoryRecord {
  id: number;
  deviceMiniotId: number;
  inventoryMiniotId: number;
  siteId: number;
  siteName?: string;
  status: DeviceStatus;
  versionFirmware?: string;
  macLan?: string;
  ipLan?: string;
  timestamp?: number;
  uptime?: number;
  actuatorStatus?: ActuatorStatus;
  ntpSynced: boolean;
  ntpServer?: string;
  createdAt: string;
  updatedAt: string;
}

interface CommandHistoryRecord {
  id: number;
  commandId: string;
  deviceMiniotId: number;
  inventoryMiniotId: number;
  siteId: number;
  userId?: number | null;
  command: string;
  target?: string | null;
  status: string;
  sentAt?: string | null;
  completedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

type HistoryTab = 'status' | 'commands';

const targetNames: Record<CommandTarget, string> = {
  router: 'Router',
  XTAM: 'Equipo XTAM',
  all: 'Todos los equipos',
};

const MiniIOTPage: React.FC = () => {
  const [sites, setSites] = useState<Site[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);
  const [selectedDevice, setSelectedDevice] = useState<MiniotDevice | null>(null);
  const [restartProgress, setRestartProgress] = useState<RestartProgress | null>(null);
  const [commandReports, setCommandReports] = useState<CommandReport[]>([]);
  const [showReportsModal, setShowReportsModal] = useState(false);
  const [selectedReportType, setSelectedReportType] = useState<CommandTarget | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusHistory, setStatusHistory] = useState<StatusHistoryRecord[]>([]);
  const [commandHistory, setCommandHistory] = useState<CommandHistoryRecord[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyTab, setHistoryTab] = useState<HistoryTab>('status');
  const [historyDateFrom, setHistoryDateFrom] = useState('');
  const [historyDateTo, setHistoryDateTo] = useState('');

  const token = localStorage.getItem('token');
  const authHeaders = { Authorization: `Bearer ${token}` };
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadSites = useCallback(async (search?: string) => {
    try {
      if (!search) setLoading(true);
      setError(null);
      const params: any = {};
      if (search) params.search = search;
      const response = await axios.get(`${API_URL}/sites/miniots`, {
        headers: authHeaders,
        params,
      });
      const sitesData: Site[] = response.data?.data ?? response.data ?? [];
      setSites(sitesData);
    } catch (e: any) {
      console.error('Error loading sites:', e);
      setError('Error al cargar los sitios. Intente nuevamente.');
    } finally {
      setLoading(false);
      setSearching(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadSites();
  }, [loadSites]);

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);

    if (!value.trim()) {
      setSearching(false);
      loadSites();
      return;
    }

    setSearching(true);
    searchDebounceRef.current = setTimeout(() => {
      loadSites(value.trim());
    }, 400);
  };

  const getLastHeartbeat = (site: Site): number => {
    const hb = site.devices?.miniots?.[0]?.status?.lastHeartbeatAt;
    return hb ? new Date(hb).getTime() : 0;
  };

  const isOnlineSite = (site: Site): boolean => {
    const st = site.devices?.miniots?.[0]?.status?.status;
    return st === 'ONLINE' || st === 'ONLINE_WITH_ISSUE' || st === 'MAINTENANCE';
  };

  const recentOnlineSites = sites
    .filter((s) => isOnlineSite(s))
    .sort((a, b) => getLastHeartbeat(b) - getLastHeartbeat(a))
    .slice(0, 10);

  const offlineSites = sites
    .filter((s) => !isOnlineSite(s))
    .sort((a, b) => getLastHeartbeat(b) - getLastHeartbeat(a))
    .slice(0, 10);

  const handleSiteSelect = (site: Site) => {
    setSelectedSite(site);
    const device = site.devices?.miniots?.[0] || null;
    setSelectedDevice(device);
    setShowHistory(false);
    setStatusHistory([]);
    setCommandHistory([]);
    setHistoryTab('status');
    setHistoryDateFrom('');
    setHistoryDateTo('');
  };

  const buildDateParams = (dateFrom?: string, dateTo?: string) => {
    const from = dateFrom ?? historyDateFrom;
    const to = dateTo ?? historyDateTo;
    const params: any = {};
    if (from) params.dateFrom = new Date(from).toISOString();
    if (to) params.dateTo = new Date(to).toISOString();
    return params;
  };

  const loadStatusHistory = async (dateFrom?: string, dateTo?: string) => {
    if (!selectedDevice) return;
    setLoadingHistory(true);
    try {
      const params = { limit: 100, ...buildDateParams(dateFrom, dateTo) };
      const response = await axios.get(
        `${API_URL}/miniot/history/device/${selectedDevice.id}`,
        { headers: authHeaders, params }
      );
      setStatusHistory(response.data?.data ?? []);
    } catch (e: any) {
      console.error('Error loading status history:', e);
    } finally {
      setLoadingHistory(false);
    }
  };

  const loadCommandHistory = async (dateFrom?: string, dateTo?: string) => {
    if (!selectedDevice) return;
    setLoadingHistory(true);
    try {
      const params = { limit: 100, ...buildDateParams(dateFrom, dateTo) };
      const response = await axios.get(
        `${API_URL}/miniot/commands/device/${selectedDevice.id}`,
        { headers: authHeaders, params }
      );
      setCommandHistory(response.data?.data ?? []);
    } catch (e: any) {
      console.error('Error loading command history:', e);
    } finally {
      setLoadingHistory(false);
    }
  };

  const loadActiveTabHistory = async (dateFrom?: string, dateTo?: string) => {
    if (historyTab === 'status') {
      await loadStatusHistory(dateFrom, dateTo);
    } else {
      await loadCommandHistory(dateFrom, dateTo);
    }
  };

  const handleToggleHistory = async () => {
    if (showHistory) {
      setShowHistory(false);
      return;
    }
    setShowHistory(true);
    await loadActiveTabHistory();
  };

  const handleTabChange = async (tab: HistoryTab) => {
    setHistoryTab(tab);
    setHistoryDateFrom('');
    setHistoryDateTo('');
    if (tab === 'status') {
      if (statusHistory.length === 0) await loadStatusHistory('', '');
    } else {
      if (commandHistory.length === 0) await loadCommandHistory('', '');
    }
  };

  const handleClearHistoryFilter = () => {
    setHistoryDateFrom('');
    setHistoryDateTo('');
    loadActiveTabHistory('', '');
  };

  const handleRefreshData = async () => {
    if (!selectedSite) return;
    setIsRefreshing(true);
    try {
      const params: any = {};
      if (searchTerm.trim()) params.search = searchTerm.trim();
      const response = await axios.get(`${API_URL}/sites/miniots`, {
        headers: authHeaders,
        params,
      });
      const sitesData: Site[] = response.data?.data ?? response.data ?? [];
      setSites(sitesData);

      const updatedSite = sitesData.find((s) => s.id === selectedSite.id);
      if (updatedSite) {
        setSelectedSite(updatedSite);
        const device = updatedSite.devices?.miniots?.[0] || null;
        setSelectedDevice(device);
      }
    } catch (e: any) {
      console.error('Error refreshing data:', e);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleSendCommand = async (target: CommandTarget) => {
    if (!selectedDevice || !selectedDevice.inventory?.serialDevice) return;

    const serial = selectedDevice.inventory.serialDevice;

    setRestartProgress({ target, progress: 0, isActive: true });

    const progressInterval = setInterval(() => {
      setRestartProgress((prev) => {
        if (!prev) return null;
        const newProgress = prev.progress + 2;
        if (newProgress >= 100) {
          clearInterval(progressInterval);
        }
        return { ...prev, progress: Math.min(newProgress, 99) };
      });
    }, 80);

    try {
      const response = await axios.post(
        `${API_URL}/miniot/commands`,
        { serial, command: 'restart', target },
        { headers: authHeaders }
      );

      clearInterval(progressInterval);
      setRestartProgress((prev) => (prev ? { ...prev, progress: 100 } : null));

      const commandId = response.data?.data?.commandId;

      setTimeout(() => {
        setRestartProgress(null);
        const report: CommandReport = {
          success: true,
          target,
          message: `Comando de reinicio enviado exitosamente para ${targetNames[target]}`,
          timestamp: new Date(),
          siteName: selectedSite?.name || 'Desconocido',
          deviceSerial: serial,
          commandId,
        };
        setCommandReports((prev) => [...prev, report]);
      }, 600);
    } catch (e: any) {
      clearInterval(progressInterval);
      setRestartProgress(null);

      const apiMsg =
        e.response?.data?.error?.message ||
        e.response?.data?.message ||
        e.message;

      const report: CommandReport = {
        success: false,
        target,
        message: apiMsg || 'Error al enviar el comando',
        timestamp: new Date(),
        siteName: selectedSite?.name || 'Desconocido',
        deviceSerial: serial,
      };
      setCommandReports((prev) => [...prev, report]);
    }
  };

  const handleSendInfoCommand = async () => {
    if (!selectedDevice || !selectedDevice.inventory?.serialDevice) return;

    const serial = selectedDevice.inventory.serialDevice;

    try {
      await axios.post(
        `${API_URL}/miniot/commands`,
        { serial, command: 'info' },
        { headers: authHeaders }
      );

      const report: CommandReport = {
        success: true,
        target: 'all',
        message: 'Comando de información enviado exitosamente',
        timestamp: new Date(),
        siteName: selectedSite?.name || 'Desconocido',
        deviceSerial: serial,
      };
      setCommandReports((prev) => [...prev, report]);

      setTimeout(() => {
        handleRefreshData();
      }, 3000);
    } catch (e: any) {
      const apiMsg =
        e.response?.data?.error?.message ||
        e.response?.data?.message ||
        e.message;

      const report: CommandReport = {
        success: false,
        target: 'all',
        message: apiMsg || 'Error al solicitar información',
        timestamp: new Date(),
        siteName: selectedSite?.name || 'Desconocido',
        deviceSerial: serial,
      };
      setCommandReports((prev) => [...prev, report]);
    }
  };

  const getReportsByTarget = (target: CommandTarget) => {
    return commandReports.filter((r) => r.target === target);
  };

  const openReportsModal = (target: CommandTarget) => {
    setSelectedReportType(target);
    setShowReportsModal(true);
  };

  const getStatusLabel = (status: DeviceStatus | undefined | null): string => {
    switch (status) {
      case 'ONLINE':
        return 'En línea';
      case 'OFFLINE':
        return 'Desconectado';
      case 'ONLINE_WITH_ISSUE':
        return 'Con problemas';
      case 'MAINTENANCE':
        return 'Mantenimiento';
      default:
        return 'Sin estado';
    }
  };

  const getStatusClass = (status: DeviceStatus | undefined | null): string => {
    switch (status) {
      case 'ONLINE':
        return 'status-online';
      case 'OFFLINE':
        return 'status-offline';
      case 'ONLINE_WITH_ISSUE':
        return 'status-warning';
      case 'MAINTENANCE':
        return 'status-maintenance';
      default:
        return 'status-unknown';
    }
  };

  const getCommandStatusLabel = (status: string): string => {
    switch (status) {
      case 'sent': return 'Enviado';
      case 'pending': return 'Pendiente';
      case 'completed': return 'Completado';
      case 'success': return 'Exitoso';
      case 'failed': return 'Fallido';
      case 'timeout': return 'Timeout';
      default: return status;
    }
  };

  const getCommandStatusClass = (status: string): string => {
    switch (status) {
      case 'completed': case 'success': return 'cmd-completed';
      case 'sent': return 'cmd-sent';
      case 'pending': return 'cmd-pending';
      case 'failed': case 'timeout': return 'cmd-failed';
      default: return 'cmd-unknown';
    }
  };

  const getCommandLabel = (cmd: string): string => {
    switch (cmd) {
      case 'restart': return 'Reinicio';
      case 'info': return 'Info';
      default: return cmd;
    }
  };

  const getTargetLabel = (target: string | null | undefined): string => {
    if (!target) return 'N/A';
    switch (target) {
      case 'router': return 'Router';
      case 'XTAM': return 'XTAM';
      case 'all': return 'Todos';
      default: return target;
    }
  };

  const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('es-CO', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return 'N/A';
    }
  };

  const formatTime = (dateStr: string | null | undefined): string => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleTimeString('es-CO', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return 'N/A';
    }
  };

  const formatUptimeHours = (seconds: number | undefined): string => {
    if (seconds === undefined || seconds === null) return 'N/A';
    const totalHours = seconds / 3600;
    if (totalHours < 1) {
      const m = Math.floor(seconds / 60);
      return `${m}min`;
    }
    return `${totalHours.toFixed(1)}h`;
  };

  const renderActuatorStatus = (actuator: ActuatorStatus | undefined) => {
    if (!actuator) return <span className="miniiot-actuator-na">N/A</span>;

    const isError = actuator.status?.toUpperCase() === 'ERROR';
    const statusClass = isError ? 'error' : 'available';

    return (
      <div className="miniiot-actuator-cell">
        <div className="miniiot-actuator-row">
          <span className={`miniiot-actuator-badge ${statusClass}`}>
            {actuator.status || '?'}
          </span>
          {actuator.count !== undefined && (
            <span className="miniiot-actuator-count">{actuator.count} conn</span>
          )}
        </div>
        {actuator.clients && actuator.clients.length > 0 && (
          <div className="miniiot-actuator-clients">
            {actuator.clients.map((c, i) => (
              <span key={i} className="miniiot-actuator-client">
                {c.ip || c.mac || '?'}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  };

  const formatDateTime = (dateStr: string | null | undefined): string => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('es-CO', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return 'N/A';
    }
  };

  const deviceStatus = selectedDevice?.status?.status;
  const isDeviceOffline = deviceStatus === 'OFFLINE' || !deviceStatus;

  return (
    <div className="miniiot-page">
      <div className="miniiot-container">
        {/* Header */}
        <div className="miniiot-header">
          <div className="miniiot-header-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="4" y="4" width="16" height="16" rx="2" ry="2" />
              <rect x="9" y="9" width="6" height="6" />
              <line x1="9" y1="1" x2="9" y2="4" />
              <line x1="15" y1="1" x2="15" y2="4" />
              <line x1="9" y1="20" x2="9" y2="23" />
              <line x1="15" y1="20" x2="15" y2="23" />
              <line x1="20" y1="9" x2="23" y2="9" />
              <line x1="20" y1="14" x2="23" y2="14" />
              <line x1="1" y1="9" x2="4" y2="9" />
              <line x1="1" y1="14" x2="4" y2="14" />
            </svg>
          </div>
          <div>
            <h1 className="miniiot-title">MiniIOT</h1>
            <p className="miniiot-subtitle">Gestión y monitoreo de dispositivos IoT</p>
          </div>
        </div>

        <div className="miniiot-layout">
          {/* Panel izquierdo - Lista de sitios */}
          <div className="miniiot-sidebar">
            <div className="miniiot-sidebar-card">
              <div className="miniiot-search-container">
                <svg className="miniiot-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                  type="text"
                  placeholder="Buscar sitio..."
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="miniiot-search-input"
                />
              </div>

              <div className="miniiot-sites-list">
                {loading ? (
                  <div className="miniiot-loading">
                    <div className="miniiot-spinner" />
                    <p>Cargando sitios...</p>
                  </div>
                ) : error ? (
                  <div className="miniiot-error">
                    <p>{error}</p>
                    <button onClick={() => loadSites()} className="miniiot-retry-btn">
                      Reintentar
                    </button>
                  </div>
                ) : sites.length === 0 ? (
                  <div className="miniiot-empty">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" opacity="0.2">
                      <rect x="4" y="4" width="16" height="16" rx="2" ry="2" />
                      <rect x="9" y="9" width="6" height="6" />
                    </svg>
                    <p>No se encontraron sitios</p>
                  </div>
                ) : (
                  <>
                    {/* Sección: Últimos reportados (Online) */}
                    <div className="miniiot-section-header online">
                      <span className="miniiot-section-dot status-online" />
                      <span className="miniiot-section-label">Últimos reportados</span>
                      <span className="miniiot-section-badge">{recentOnlineSites.length}</span>
                    </div>
                    {recentOnlineSites.length === 0 ? (
                      <div className="miniiot-section-empty">
                        <p>Sin sitios en línea</p>
                      </div>
                    ) : (
                      recentOnlineSites.map((site) => {
                        const device = site.devices?.miniots?.[0];
                        const isSelected = selectedSite?.id === site.id;
                        const devStatus = device?.status?.status;

                        return (
                          <button
                            key={site.id}
                            onClick={() => handleSiteSelect(site)}
                            className={`miniiot-site-item ${isSelected ? 'selected' : ''}`}
                          >
                            <div className="miniiot-site-item-content">
                              <div className="miniiot-site-item-info">
                                <div className="miniiot-site-item-name">{site.name}</div>
                                <div className="miniiot-site-item-neighborhood">
                                  {site.neighborhood || site.address}
                                </div>
                                <div className="miniiot-site-item-serial">
                                  {device?.inventory?.serialDevice || 'Sin serial'}
                                </div>
                              </div>
                              <div className={`miniiot-status-dot ${getStatusClass(devStatus)}`} title={getStatusLabel(devStatus)} />
                            </div>
                          </button>
                        );
                      })
                    )}

                    {/* Separador */}
                    <div className="miniiot-section-divider" />

                    {/* Sección: Offline / Sin reporte */}
                    <div className="miniiot-section-header offline">
                      <span className="miniiot-section-dot status-offline" />
                      <span className="miniiot-section-label">Offline / Sin reporte</span>
                      <span className="miniiot-section-badge">{offlineSites.length}</span>
                    </div>
                    {offlineSites.length === 0 ? (
                      <div className="miniiot-section-empty">
                        <p>Sin sitios offline</p>
                      </div>
                    ) : (
                      offlineSites.map((site) => {
                        const device = site.devices?.miniots?.[0];
                        const isSelected = selectedSite?.id === site.id;
                        const devStatus = device?.status?.status;

                        return (
                          <button
                            key={site.id}
                            onClick={() => handleSiteSelect(site)}
                            className={`miniiot-site-item ${isSelected ? 'selected' : ''}`}
                          >
                            <div className="miniiot-site-item-content">
                              <div className="miniiot-site-item-info">
                                <div className="miniiot-site-item-name">{site.name}</div>
                                <div className="miniiot-site-item-neighborhood">
                                  {site.neighborhood || site.address}
                                </div>
                                <div className="miniiot-site-item-serial">
                                  {device?.inventory?.serialDevice || 'Sin serial'}
                                </div>
                              </div>
                              <div className={`miniiot-status-dot ${getStatusClass(devStatus)}`} title={getStatusLabel(devStatus)} />
                            </div>
                          </button>
                        );
                      })
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Panel derecho - Detalles */}
          <div className="miniiot-main">
            {selectedSite && selectedDevice ? (
              <div className="miniiot-detail-panels">
                {/* Info del dispositivo */}
                <div className="miniiot-card">
                  <div className="miniiot-card-header">
                    <div>
                      <h2 className="miniiot-card-title">{selectedSite.name}</h2>
                      <p className="miniiot-card-subtitle">{selectedSite.address}</p>
                    </div>
                    <div className="miniiot-device-status-badge">
                      <span className={`miniiot-status-indicator ${getStatusClass(deviceStatus)}`} />
                      <span className={`miniiot-status-text ${getStatusClass(deviceStatus)}`}>
                        {getStatusLabel(deviceStatus)}
                      </span>
                    </div>
                  </div>

                  <div className="miniiot-info-grid">
                    <div className="miniiot-info-item">
                      <div className="miniiot-info-label">SERIAL</div>
                      <div className="miniiot-info-value mono">
                        {selectedDevice.inventory?.serialDevice || 'N/A'}
                      </div>
                    </div>

                    <div className="miniiot-info-item">
                      <div className="miniiot-info-label">MODELO</div>
                      <div className="miniiot-info-value">
                        {selectedDevice.inventory?.modelName || 'N/A'}
                      </div>
                    </div>

                    <div className="miniiot-info-item">
                      <div className="miniiot-info-label">FIRMWARE</div>
                      <div className="miniiot-info-value mono">
                        {selectedDevice.inventory?.firmware || 'N/A'}
                      </div>
                    </div>

                    <div className="miniiot-info-item">
                      <div className="miniiot-info-label">MAC</div>
                      <div className="miniiot-info-value mono">
                        {selectedDevice.inventory?.mac || 'N/A'}
                      </div>
                    </div>

                    <div className="miniiot-info-item">
                      <div className="miniiot-info-label-with-action">
                        <div className="miniiot-info-label-icon">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                          </svg>
                          <span className="miniiot-info-label">ÚLTIMO HEARTBEAT</span>
                        </div>
                        <button
                          onClick={handleRefreshData}
                          disabled={isRefreshing}
                          className="miniiot-refresh-btn"
                          title="Actualizar información"
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className={isRefreshing ? 'spin' : ''}
                          >
                            <polyline points="23 4 23 10 17 10" />
                            <polyline points="1 20 1 14 7 14" />
                            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                          </svg>
                        </button>
                      </div>
                      <div className="miniiot-info-date-block">
                        <div className="miniiot-info-value">
                          <span className="miniiot-info-date-label">Fecha: </span>
                          {formatDate(selectedDevice.status?.lastHeartbeatAt)}
                        </div>
                        <div className="miniiot-info-value">
                          <span className="miniiot-info-date-label">Hora: </span>
                          {formatTime(selectedDevice.status?.lastHeartbeatAt)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Opciones de reinicio */}
                <div className="miniiot-card">
                  <h3 className="miniiot-section-title">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="23 4 23 10 17 10" />
                      <polyline points="1 20 1 14 7 14" />
                      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                    </svg>
                    Comandos
                  </h3>

                  <div className="miniiot-commands-grid">
                    {/* Router */}
                    <div className="miniiot-command-group">
                      <button
                        onClick={() => handleSendCommand('router')}
                        disabled={restartProgress !== null || isDeviceOffline}
                        className="miniiot-command-btn"
                      >
                        <div className="miniiot-command-icon blue">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="23 4 23 10 17 10" />
                            <polyline points="1 20 1 14 7 14" />
                            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                          </svg>
                        </div>
                        <div className="miniiot-command-text">
                          <div className="miniiot-command-name">Router</div>
                          <div className="miniiot-command-desc">Reiniciar router de red</div>
                        </div>
                      </button>
                      {getReportsByTarget('router').length > 0 && (
                        <div className="miniiot-report-link">
                          <button onClick={() => openReportsModal('router')} className="miniiot-report-link-btn">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                            </svg>
                            <span>Ver Informe ({getReportsByTarget('router').length})</span>
                          </button>
                        </div>
                      )}
                    </div>

                    {/* XTAM */}
                    <div className="miniiot-command-group">
                      <button
                        onClick={() => handleSendCommand('XTAM')}
                        disabled={restartProgress !== null || isDeviceOffline}
                        className="miniiot-command-btn"
                      >
                        <div className="miniiot-command-icon red">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="23 4 23 10 17 10" />
                            <polyline points="1 20 1 14 7 14" />
                            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                          </svg>
                        </div>
                        <div className="miniiot-command-text">
                          <div className="miniiot-command-name">Equipo XTAM</div>
                          <div className="miniiot-command-desc">Reiniciar servidor XTAM</div>
                        </div>
                      </button>
                      {getReportsByTarget('XTAM').length > 0 && (
                        <div className="miniiot-report-link">
                          <button onClick={() => openReportsModal('XTAM')} className="miniiot-report-link-btn">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                            </svg>
                            <span>Ver Informe ({getReportsByTarget('XTAM').length})</span>
                          </button>
                        </div>
                      )}
                    </div>

                    {/* All */}
                    <div className="miniiot-command-group">
                      <button
                        onClick={() => handleSendCommand('all')}
                        disabled={restartProgress !== null || isDeviceOffline}
                        className="miniiot-command-btn"
                      >
                        <div className="miniiot-command-icon yellow">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="23 4 23 10 17 10" />
                            <polyline points="1 20 1 14 7 14" />
                            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                          </svg>
                        </div>
                        <div className="miniiot-command-text">
                          <div className="miniiot-command-name">Todos</div>
                          <div className="miniiot-command-desc">Reiniciar todos los equipos</div>
                        </div>
                      </button>
                      {getReportsByTarget('all').length > 0 && (
                        <div className="miniiot-report-link">
                          <button onClick={() => openReportsModal('all')} className="miniiot-report-link-btn">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                            </svg>
                            <span>Ver Informe ({getReportsByTarget('all').length})</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Botón de solicitar info */}
                  <div className="miniiot-info-command-section">
                    <button
                      onClick={handleSendInfoCommand}
                      disabled={restartProgress !== null || isDeviceOffline}
                      className="miniiot-info-command-btn"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="16" x2="12" y2="12" />
                        <line x1="12" y1="8" x2="12.01" y2="8" />
                      </svg>
                      Solicitar Información del Dispositivo
                    </button>
                  </div>

                  {/* Barra de progreso */}
                  {restartProgress && (
                    <div className="miniiot-progress-container">
                      <div className="miniiot-progress-header">
                        <div className="miniiot-progress-label">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="spin">
                            <polyline points="23 4 23 10 17 10" />
                            <polyline points="1 20 1 14 7 14" />
                            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                          </svg>
                          <span>Reiniciando {targetNames[restartProgress.target]}...</span>
                        </div>
                        <span className="miniiot-progress-percent">
                          {restartProgress.progress}%
                        </span>
                      </div>
                      <div className="miniiot-progress-bar">
                        <div
                          className="miniiot-progress-fill"
                          style={{ width: `${restartProgress.progress}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Botón de Historial */}
                <div className="miniiot-card">
                  <button
                    onClick={handleToggleHistory}
                    disabled={loadingHistory}
                    className="miniiot-history-toggle-btn"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                    {loadingHistory ? 'Cargando historial...' : showHistory ? 'Ocultar Historial' : 'Ver Historial'}
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`miniiot-chevron ${showHistory ? 'open' : ''}`}>
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>
                </div>

                {/* Panel de Historial */}
                {showHistory && (
                  <div className="miniiot-card miniiot-history-panel">
                    {/* Pestañas */}
                    <div className="miniiot-history-tabs">
                      <button
                        className={`miniiot-history-tab ${historyTab === 'status' ? 'active' : ''}`}
                        onClick={() => handleTabChange('status')}
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                        </svg>
                        Reportes de Estado
                        <span className="miniiot-tab-count">{statusHistory.length}</span>
                      </button>
                      <button
                        className={`miniiot-history-tab ${historyTab === 'commands' ? 'active' : ''}`}
                        onClick={() => handleTabChange('commands')}
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="4 17 10 11 4 5" />
                          <line x1="12" y1="19" x2="20" y2="19" />
                        </svg>
                        Comandos Enviados
                        <span className="miniiot-tab-count">{commandHistory.length}</span>
                      </button>
                    </div>

                    {/* Filtros de fecha (compartidos) */}
                    <div className="miniiot-history-filters">
                      <div className="miniiot-history-filter-group">
                        <label className="miniiot-filter-label">Desde</label>
                        <input
                          type="datetime-local"
                          value={historyDateFrom}
                          onChange={(e) => setHistoryDateFrom(e.target.value)}
                          className="miniiot-filter-input"
                        />
                      </div>
                      <div className="miniiot-history-filter-group">
                        <label className="miniiot-filter-label">Hasta</label>
                        <input
                          type="datetime-local"
                          value={historyDateTo}
                          onChange={(e) => setHistoryDateTo(e.target.value)}
                          className="miniiot-filter-input"
                        />
                      </div>
                      <button
                        onClick={() => loadActiveTabHistory()}
                        disabled={loadingHistory}
                        className="miniiot-filter-btn apply"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="11" cy="11" r="8" />
                          <line x1="21" y1="21" x2="16.65" y2="16.65" />
                        </svg>
                        Filtrar
                      </button>
                      {(historyDateFrom || historyDateTo) && (
                        <button
                          onClick={handleClearHistoryFilter}
                          disabled={loadingHistory}
                          className="miniiot-filter-btn clear"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                          </svg>
                          Limpiar
                        </button>
                      )}
                    </div>

                    {/* Contenido: Reportes de Estado */}
                    {historyTab === 'status' && (
                      <>
                        {statusHistory.length === 0 ? (
                          <div className="miniiot-history-empty">
                            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" opacity="0.2">
                              <circle cx="12" cy="12" r="10" />
                              <polyline points="12 6 12 12 16 14" />
                            </svg>
                            <p>Sin registros de estado</p>
                          </div>
                        ) : (
                          <div className="miniiot-history-table-wrapper">
                            <table className="miniiot-history-table">
                              <thead>
                                <tr>
                                  <th>Fecha</th>
                                  <th>Estado</th>
                                  <th>Uptime</th>
                                  <th>Actuator</th>
                                  <th>NTP</th>
                                </tr>
                              </thead>
                              <tbody>
                                {statusHistory.map((record) => (
                                  <tr key={record.id}>
                                    <td className="miniiot-history-date">{formatDateTime(record.createdAt)}</td>
                                    <td>
                                      <span className={`miniiot-history-status-badge ${getStatusClass(record.status)}`}>
                                        {getStatusLabel(record.status)}
                                      </span>
                                    </td>
                                    <td className="miniiot-uptime-cell">{formatUptimeHours(record.uptime)}</td>
                                    <td>{renderActuatorStatus(record.actuatorStatus)}</td>
                                    <td>
                                      <span className={`miniiot-ntp-badge ${record.ntpSynced ? 'synced' : 'not-synced'}`}>
                                        {record.ntpSynced ? 'Sync' : 'No'}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </>
                    )}

                    {/* Contenido: Historial de Comandos */}
                    {historyTab === 'commands' && (
                      <>
                        {commandHistory.length === 0 ? (
                          <div className="miniiot-history-empty">
                            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" opacity="0.2">
                              <polyline points="4 17 10 11 4 5" />
                              <line x1="12" y1="19" x2="20" y2="19" />
                            </svg>
                            <p>Sin comandos registrados</p>
                          </div>
                        ) : (
                          <div className="miniiot-history-table-wrapper">
                            <table className="miniiot-history-table">
                              <thead>
                                <tr>
                                  <th>Fecha</th>
                                  <th>Comando</th>
                                  <th>Target</th>
                                  <th>Estado</th>
                                  <th>Enviado</th>
                                  <th>Completado</th>
                                </tr>
                              </thead>
                              <tbody>
                                {commandHistory.map((record) => (
                                  <tr key={record.id}>
                                    <td className="miniiot-history-date">{formatDateTime(record.createdAt)}</td>
                                    <td>
                                      <span className="miniiot-cmd-type-badge">{getCommandLabel(record.command)}</span>
                                    </td>
                                    <td>{getTargetLabel(record.target)}</td>
                                    <td>
                                      <span className={`miniiot-cmd-status-badge ${getCommandStatusClass(record.status)}`}>
                                        {getCommandStatusLabel(record.status)}
                                      </span>
                                    </td>
                                    <td className="miniiot-history-date">{formatDateTime(record.sentAt)}</td>
                                    <td className="miniiot-history-date">{formatDateTime(record.completedAt)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="miniiot-empty-state">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" opacity="0.15">
                  <rect x="4" y="4" width="16" height="16" rx="2" ry="2" />
                  <rect x="9" y="9" width="6" height="6" />
                  <line x1="9" y1="1" x2="9" y2="4" />
                  <line x1="15" y1="1" x2="15" y2="4" />
                  <line x1="9" y1="20" x2="9" y2="23" />
                  <line x1="15" y1="20" x2="15" y2="23" />
                  <line x1="20" y1="9" x2="23" y2="9" />
                  <line x1="20" y1="14" x2="23" y2="14" />
                  <line x1="1" y1="9" x2="4" y2="9" />
                  <line x1="1" y1="14" x2="4" y2="14" />
                </svg>
                <h3 className="miniiot-empty-title">Selecciona un sitio</h3>
                <p className="miniiot-empty-desc">
                  Elige un sitio de la lista para ver los detalles del dispositivo MiniIOT y ejecutar comandos
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal de Informes */}
      {showReportsModal && selectedReportType && (
        <div className="miniiot-modal-overlay" onClick={() => setShowReportsModal(false)}>
          <div className="miniiot-modal" onClick={(e) => e.stopPropagation()}>
            <div className="miniiot-modal-header">
              <div className="miniiot-modal-header-info">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                </svg>
                <div>
                  <h2 className="miniiot-modal-title">Informes de Reinicio</h2>
                  <p className="miniiot-modal-subtitle">
                    {targetNames[selectedReportType]} - Historial de comandos enviados
                  </p>
                </div>
              </div>
              <button onClick={() => setShowReportsModal(false)} className="miniiot-modal-close">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="miniiot-modal-body">
              {getReportsByTarget(selectedReportType)
                .slice()
                .reverse()
                .map((report, index) => (
                  <div
                    key={index}
                    className={`miniiot-report-item ${report.success ? 'success' : 'error'}`}
                  >
                    <div className="miniiot-report-item-header">
                      <div className="miniiot-report-item-icon">
                        {report.success ? (
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                            <polyline points="22 4 12 14.01 9 11.01" />
                          </svg>
                        ) : (
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="15" y1="9" x2="9" y2="15" />
                            <line x1="9" y1="9" x2="15" y2="15" />
                          </svg>
                        )}
                      </div>
                      <div className="miniiot-report-item-details">
                        <div className="miniiot-report-item-title-row">
                          <h3 className="miniiot-report-item-title">
                            {targetNames[report.target]}
                          </h3>
                          <span className={`miniiot-report-badge ${report.success ? 'success' : 'error'}`}>
                            {report.success ? 'Enviado' : 'Fallido'}
                          </span>
                        </div>
                        <p className={`miniiot-report-message ${report.success ? 'success' : 'error'}`}>
                          {report.message}
                        </p>
                        <div className="miniiot-report-meta">
                          <div className="miniiot-report-meta-item">
                            <span className="miniiot-report-meta-label">Sitio:</span>
                            <span className="miniiot-report-meta-value">{report.siteName}</span>
                          </div>
                          <div className="miniiot-report-meta-item">
                            <span className="miniiot-report-meta-label">Dispositivo:</span>
                            <span className="miniiot-report-meta-value mono">{report.deviceSerial}</span>
                          </div>
                          <div className="miniiot-report-meta-item">
                            <span className="miniiot-report-meta-label">Fecha:</span>
                            <span className="miniiot-report-meta-value">
                              {report.timestamp.toLocaleDateString('es-CO', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                              })}
                            </span>
                          </div>
                          <div className="miniiot-report-meta-item">
                            <span className="miniiot-report-meta-label">Hora:</span>
                            <span className="miniiot-report-meta-value">
                              {report.timestamp.toLocaleTimeString('es-CO', {
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit',
                              })}
                            </span>
                          </div>
                          {report.commandId && (
                            <div className="miniiot-report-meta-item full">
                              <span className="miniiot-report-meta-label">Command ID:</span>
                              <span className="miniiot-report-meta-value mono">{report.commandId}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

              {getReportsByTarget(selectedReportType).length === 0 && (
                <div className="miniiot-modal-empty">
                  <p>No hay informes para este tipo de comando</p>
                </div>
              )}
            </div>

            <div className="miniiot-modal-footer">
              <p className="miniiot-modal-count">
                Total de comandos:{' '}
                <span>{getReportsByTarget(selectedReportType).length}</span>
              </p>
              <button onClick={() => setShowReportsModal(false)} className="miniiot-modal-close-btn">
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MiniIOTPage;
